import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Link,
  Stack
} from '@mui/material';

interface CandidateItem {
  id: string;
  name: string;
}

interface RequestingUser {
  userID?: string;
  displayName?: string;
  userName?: string | null;
}

export interface WantListEntryListItem {
  id: string;
  requestingUserID?: string;
  requestingUserName?: string | null;
  requestingUser?: RequestingUser | null;
  candidateOfferedItemIDs?: string[];
  candidateItems?: CandidateItem[];
  cashOfferUSD?: number;
  targetItemID?: string;
  targetItemName?: string | null;
  status?: string;
}

interface WantListEntryListModalProps {
  open: boolean;
  onClose: () => void;
  itemName: string;
  itemId?: string;
  interests: WantListEntryListItem[];
}

const WantListEntryListModal: React.FC<WantListEntryListModalProps> = ({ open, onClose, itemName, itemId, interests }) => {
  const navigate = useNavigate();

  const openTraderProfile = (userId?: string) => {
    if (!userId) return;
    onClose();
    navigate(`/users/${userId}`);
  };

  const openEditItem = () => {
    if (!itemId) return;
    onClose();
    navigate(`/edit-item/${itemId}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Saved Wants for {itemName}</DialogTitle>
      <DialogContent>
        {interests.length === 0 ? (
          <Typography color="text.secondary">No one has added this item to a want list yet.</Typography>
        ) : (
          <List>
            {interests.map((interest, index) => {
              const candidateItems = interest.candidateItems || [];
              const traderName = interest.requestingUser?.displayName || interest.requestingUserName || interest.requestingUserID || 'Unknown Trader';
              const traderUserName = interest.requestingUser?.userName;

              return (
                <React.Fragment key={interest.id}>
                  <ListItem alignItems="flex-start" disableGutters sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {traderName}
                          </Typography>
                          {traderUserName && (
                            <Typography variant="body2" color="text.secondary">
                              @{traderUserName}
                            </Typography>
                          )}
                          <Chip size="small" label={interest.status || 'active'} color={interest.status === 'active' ? 'success' : 'default'} />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1.25 }}>
                          {interest.requestingUserID && (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => openTraderProfile(interest.requestingUserID)}
                              sx={{ mb: 1, display: 'inline-block' }}
                            >
                              View trader profile
                            </Link>
                          )}

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Their offer
                          </Typography>

                          {candidateItems.length > 0 ? (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              {candidateItems.map((item) => (
                                <Chip key={item.id} size="small" variant="outlined" label={item.name || item.id} />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No offered items listed.
                            </Typography>
                          )}

                          {Number(interest.cashOfferUSD || 0) > 0 && (
                            <Chip
                              sx={{ mt: 1 }}
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`Cash +$${Number(interest.cashOfferUSD).toFixed(2)}`}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < interests.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        {itemId && (
          <Button onClick={openEditItem} variant="outlined">
            Edit Item
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WantListEntryListModal;
